import { Inject, Injectable } from '@angular/core';
import { OlaMaps } from '@app/ola-maps';
import { ContainerModel, MapViewport } from '@app/models/server/layout.model';
import { DOCUMENT } from '@angular/common';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { SelectedItem } from '@app/models/client/item.model';
import { environment } from '@src/environments/environment';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { ToastService } from '@app/services/util-services/root-services/toast-service';

@Injectable()
export class B2bOlaMapServices {
  private olaMaps: OlaMaps;
  private listPageMap: any;
  private listPageMapContainer: string;
  private lastClickedMarker: ContainerModel;
  private currentClickedMarker: ContainerModel;

  private activePropertyMarkers: any[] = [];
  private activeLocalityMarkers: any[] = [];

  circleData = {
    type: 'FeatureCollection',
    features: [],
  };

  polygonData = {
    type: 'FeatureCollection',
    features: [],
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private eventService: EventService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    private toastService: ToastService,
  ) {}

  initOlaMaps(): OlaMaps {
    if (!this.olaMaps) {
      this.olaMaps = new OlaMaps({
        apiKey: environment.OLA.OLA_API_KEY,
      });
    }
    return this.olaMaps;
  }

  createListPageMap(
    containerName: string,
    center: { lat: number; lng: number },
    zoom: number,
  ) {
    try {
      if (!this.listPageMapContainer) {
        this.listPageMapContainer = containerName;
      }

      if (!this.listPageMap || this.listPageMapContainer !== containerName) {
        this.listPageMap = this.olaMaps.init({
          style: environment.OLA.OLA_MAP_STYLE,
          container: containerName,
          // center: [center?.lng, center?.lat],
          zoom,
        });
        this.listPageMapContainer = containerName;
      }

      this.setMapCenter(center);
    } catch (e) {
      console.warn('Error is ', e);
      if (!this.b2bDeviceUtilService.isWebglSupported()) {
        this.toastService.error(
          'Please enable hardware acceleration in your browser settings to view this content properly.',
          5000,
          'Graphics support disabled',
        );
      }
    }

    return this.listPageMap;
  }

  setMapCenter(center: { lat: number; lng: number }) {
    this.listPageMap.setCenter(center);
  }

  resetMapWithNewData(
    containers: ContainerModel[],
    locations: SelectedItem[],
    center: { lat: number; lng: number },
  ) {
    if (this.activePropertyMarkers?.length > 0) {
      for (const marker of this.activePropertyMarkers) {
        this.removeMarker(marker);
      }
    }

    this.activePropertyMarkers = [];

    if (this.activeLocalityMarkers?.length > 0) {
      for (const marker of this.activeLocalityMarkers) {
        this.removeMarker(marker);
      }
    }

    this.activeLocalityMarkers = [];

    this.resetCircles();

    this.circleData.features = [];
    this.polygonData.features = [];

    if (locations?.length > 0) {
      const newCenter: { lat: number; lng: number } = {
        lat: locations[0]?.value?.location?.coordinates?.[1],
        lng: locations[0]?.value?.location?.coordinates?.[0],
      };
      this.moveTo(newCenter);
      for (const location of locations) {
        const circleCenter: { lat: number; lng: number } = {
          lat: location?.value?.location?.coordinates?.[1],
          lng: location?.value?.location?.coordinates?.[0],
        };
        this.draw(circleCenter, location?.title, location?.value?.viewPort);
        this.setLocationMarker(circleCenter, location?._id, location?.title);
      }
    } else {
      // this.setMapCenter(center);
      this.moveTo(center, 10);
    }

    this.setPropertyMarkers(containers);
  }

  draw(
    newCenter: { lat: number; lng: number },
    locationName: string,
    viewport: MapViewport,
  ) {
    /*if (
      viewport?.southwest?.lng &&
      viewport?.southwest?.lat &&
      viewport?.northeast?.lng &&
      viewport?.northeast?.lat
    ) {
      this.drawPolygon(newCenter, locationName, viewport);
    } else {
      this.drawCircle(newCenter, locationName, viewport);
    }*/
    this.drawCircle(newCenter, locationName, viewport);
  }

  drawPolygon(
    newCenter: { lat: number; lng: number },
    locationName: string,
    viewport: MapViewport,
  ) {
    let sw: number[] = [];
    let ne: number[] = [];
    let llb: any;
    if (
      viewport?.southwest?.lng &&
      viewport?.southwest?.lat &&
      viewport?.northeast?.lng &&
      viewport?.northeast?.lat
    ) {
      sw = [viewport?.southwest?.lng, viewport?.southwest?.lat];
      ne = [viewport?.northeast?.lng, viewport?.northeast?.lat];
      llb = new this.olaMaps.mapInstance.LngLatBounds(
        new this.olaMaps.mapInstance.LngLat(
          viewport?.southwest?.lng,
          viewport?.southwest?.lat,
        ),
        new this.olaMaps.mapInstance.LngLat(
          viewport?.northeast?.lng,
          viewport?.northeast?.lat,
        ),
      );
      // const llb = new this.olaMaps.mapInstance.LngLatBounds(sw, ne);
    }

    this.polygonData.features.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            sw, // SW
            [sw[0], ne[1]], // NW
            ne, // NE
            [ne[0], sw[1]], // SE
            sw, // Closing the loop
          ],
        ],
      },
      properties: {
        name: locationName, // Store the location name
      },
    });

    if (this.listPageMap.getSource('polygon-source')) {
      this.listPageMap.getSource('polygon-source').setData(this.polygonData);
    } else {
      this.listPageMap.addSource('polygon-source', {
        type: 'geojson',
        data: this.polygonData,
      });
      this.listPageMap.addLayer({
        id: 'polygon-layer',
        type: 'fill',
        source: 'polygon-source',
        paint: {
          'fill-color': '#FF0000', // Red fill
          'fill-opacity': 0.2, // Semi-transparent
          'fill-outline-color': '#FF0000', // Red border
        },
      });
    }

    // ✅ Fit map to the bounds
    // this.listPageMap.fitBounds(llb, { padding: 20 });
  }

  drawCircle(
    newCenter: { lat: number; lng: number },
    locationName: string,
    viewport: MapViewport,
  ) {
    this.circleData.features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [newCenter?.lng, newCenter?.lat],
      },
      properties: {
        name: locationName, // Store the location name
      },
    });

    if (this.listPageMap.getSource('circle-source')) {
      this.listPageMap.getSource('circle-source').setData(this.circleData);
    } else {
      this.listPageMap.addSource('circle-source', {
        type: 'geojson',
        data: this.circleData,
      });
      this.listPageMap.addLayer({
        id: 'circle-layer',
        type: 'circle',
        source: 'circle-source',
        paint: {
          /*'circle-radius': [
          // 1st way
            'interpolate',
            ['linear'],
            ['zoom'],
            5,
            5000 / 2, // At zoom level 5, radius is 5000 meters
            10,
            1000 / 2, // At zoom level 10, radius is 1000 meters
            15,
            500 / 2, // At zoom level 15, radius is 500 meters
          ],
          'circle-pitch-scale': 'map', // Ensures size scales with the map*/
          // 2nd way
          'circle-radius': 50, // Adjust size as needed
          'circle-color': '#ffcba5', // Fill color
          'circle-opacity': 0.5, // Adjust opacity if needed
          'circle-stroke-color': '#FF6B00', // Border color (black)
          'circle-stroke-width': 3, // Border thickness
          'circle-stroke-opacity': 1, // Ensure the border is visible
        },
      });
    }

    // ✅ Fit map to the bounds
    // this.listPageMap.fitBounds(llb, { padding: 20 });
  }

  resetCircles() {
    // Remove the circle layer if it exists
    if (this.listPageMap?.getLayer('circle-layer')) {
      this.listPageMap?.removeLayer('circle-layer');
    }

    // Remove the circle source if it exists
    if (this.listPageMap?.getSource('circle-source')) {
      this.listPageMap?.removeSource('circle-source');
    }

    // Clear the feature collection
    this.circleData.features = [];
  }

  getMapZoom(): number {
    return this.listPageMap?.getZoom();
  }

  moveTo(newCenter: { lat: number; lng: number }, zoom: number = 13) {
    this.listPageMap.flyTo({
      center: [newCenter?.lng, newCenter?.lat],
      zoom,
      // speed: 0.2,
      curve: 1,
      easing(t) {
        return t;
      },
    });
  }

  setLocationMarker(
    newCenter: { lat: number; lng: number },
    locationId: string,
    locationName: string,
  ) {
    const prefix: string = String('loc-marker-');
    if (newCenter?.lat && newCenter?.lng) {
      // const iconDiv = this.document?.getElementById(prefix + locationId);

      /*if (iconDiv) {
        return;
      }*/

      /*const icon = this.document?.createElement('div');
      icon.id = prefix + locationId;
      icon.innerText = locationName?.substring(0, 2);

      icon.classList.add('location-mk-icon');*/
      // TODO
      const popupContent = `
        <div class="pin">${locationName}</div>
      `;

      /*icon.onclick = () => {
        // TODO incase if we want to zoom on locality or something
      };*/

      const popup = this.createPopup(
        { offset: [0, 0], anchor: 'bottom', className: 'lp-loc-nm' },
        popupContent,
      );

      this.addMarker(
        { anchor: 'bottom', color: '#FF6B00', className: 'lp-loc-mk' },
        { lat: newCenter?.lat, lng: newCenter?.lng },
        popup,
        this.listPageMap,
        'LOCALITY',
      );
    } else {
    }
  }

  setPropertyMarkers(containers: ContainerModel[]) {
    if (containers?.length > 0) {
      for (const container of containers) {
        this.setPropertyMarker(container);
      }
    }
  }

  setPropertyMarker(container: ContainerModel) {
    if (container?.lat && container?.lng) {
      const iconDiv = this.document?.getElementById('marker-' + container?.uid);

      if (iconDiv) {
        return;
      }

      const propertyIcon = this.document?.createElement('div');
      propertyIcon.id = 'marker-' + container?.uid;

      if (container?.priceDetail?.listPageOfferedRate > 0) {
        propertyIcon.innerText = `Rs ${container?.priceDetail?.listPageOfferedRate}`;
      } else {
        // TODO what to show if price is not available
      }

      propertyIcon.classList.add('property-rate');
      // TODO
      const popupContent = `
        <div>Name: ${container?.displayName}</div>
        <div>Remove tooltip in future after testing</div>
      `;

      propertyIcon.onclick = () => {
        // this.onSelectPropertyMarker(container);
        this.eventService.markerClickEvent.emit({
          container,
        });
      };

      const popup = this.createPopup(
        { offset: [0, 0], anchor: 'bottom' },
        popupContent,
      );

      this.addMarker(
        { element: propertyIcon, anchor: 'bottom' },
        { lat: container?.lat, lng: container?.lng },
        null,
        this.listPageMap,
        'PROPERTY',
      );
    } else {
    }
  }

  onSelectPropertyMarker(containerModel: ContainerModel) {
    if (!this.lastClickedMarker) {
      this.lastClickedMarker = containerModel;
    }

    this.currentClickedMarker = containerModel;

    this.changeMarkerState();
  }

  changeMarkerState() {
    if (this.lastClickedMarker?.uid === this.currentClickedMarker?.uid) {
      // make current active
      const propertyIcon: HTMLElement = this.document?.getElementById(
        'marker-' + this.currentClickedMarker?.uid,
      );

      if (propertyIcon) {
        propertyIcon.classList.remove('viewed-mk');
        propertyIcon.classList.add('active-mk');
      }
    } else {
      const cPropertyIcon: HTMLElement = this.document?.getElementById(
        'marker-' + this.currentClickedMarker?.uid,
      );

      if (cPropertyIcon) {
        cPropertyIcon.classList.remove('viewed-mk');
        cPropertyIcon.classList.add('active-mk');
      }

      const lPropertyIcon: HTMLElement = this.document?.getElementById(
        'marker-' + this.lastClickedMarker?.uid,
      );

      if (lPropertyIcon) {
        lPropertyIcon.classList.remove('active-mk');
        lPropertyIcon.classList.add('viewed-mk');
      }

      this.lastClickedMarker = this.currentClickedMarker;
    }
  }

  createPopup(options, htmlContent) {
    return this.olaMaps?.addPopup(options)?.setHTML(htmlContent);
  }

  removeMarker(marker) {
    marker?.remove();
  }

  addMarker(
    options,
    latLng: { lat: number; lng: number },
    popup: any,
    addToMap,
    markerType: 'PROPERTY' | 'LOCALITY',
  ) {
    try {
      const marker = this.olaMaps?.addMarker(options);
      marker?.setLngLat([latLng?.lng, latLng?.lat]);
      marker?.setPopup(popup);
      marker?.addTo(addToMap);
      if (markerType === 'PROPERTY') {
        this.activePropertyMarkers.push(marker);
      } else if (markerType === 'LOCALITY') {
        this.activeLocalityMarkers.push(marker);
      } else {
      }
    } catch (e) {}
  }

  activatePropertyMarker(container: ContainerModel) {
    this.onSelectPropertyMarker(container);
  }

  destroy() {
    this.olaMaps = undefined;
    this.listPageMap = undefined;
    this.listPageMapContainer = undefined;
    this.lastClickedMarker = undefined;
    this.currentClickedMarker = undefined;
  }
}
